import Typography from '@mui/material/Typography';
import { useConfirm } from 'material-ui-confirm';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import {
  DashboardPageToolbar,
  DashboardToolbarAside,
} from 'src/common/components/DashboardPageToolbar';
import { LanguageKeys } from 'src/common/constants/languages';
import dayjs from 'src/helpers/dayjs';
import { IPendingClaim } from 'src/interfaces/PendingClaim';
import PendingClaimParticipantsTable from 'src/pages/Dashboard/UserParticipants/components/PendingClaimParticipantsTable';
import useParticipantsStore from 'src/stores/participantsStore';
import useSystemOptionsStore from 'src/stores/systemOptionsStore';

type Props = {
  claims: IPendingClaim[];
};

const PendingClaimsSection = ({ claims }: Props) => {
  const { t, i18n } = useTranslation();
  const confirm = useConfirm();
  const { enqueueSnackbar } = useSnackbar();
  const { abandonClaimParticipant } = useParticipantsStore();
  const { dateTimeFormat } = useSystemOptionsStore(s => s);
  const currentLang = (i18n.resolvedLanguage || LanguageKeys.ro) as LanguageKeys;

  if (!claims.length) {
    return null;
  }

  const formatDate = (value: string) =>
    dayjs(value).locale(currentLang).format(dateTimeFormat[currentLang]);

  const handleAbandonParticipant = async (claim: IPendingClaim, participantId: string, name: string) => {
    const { confirmed } = await confirm({
      title: t('participants.claim.pending.abandonParticipantConfirmTitle'),
      description: t('participants.claim.pending.abandonParticipantConfirmDescription', { name }),
      confirmationText: t('participants.claim.pending.abandonParticipant'),
      cancellationText: t('base.cancel'),
    });
    if (!confirmed) {
      return;
    }
    try {
      await abandonClaimParticipant(claim.id, participantId);
      enqueueSnackbar(t('participants.claim.pending.abandonParticipantSuccess'), { variant: 'success' });
    } catch (error: unknown) {
      const response = (error as { response?: { data?: { message?: string } } })?.response;
      enqueueSnackbar(
        response?.data?.message || t('participants.claim.pending.abandonParticipantFailed'),
        { variant: 'error' },
      );
    }
  };

  return (
    <>
      {claims.map(claim => (
        <div key={claim.id}>
          <DashboardPageToolbar>
            <DashboardToolbarAside variant="full">
              <Typography variant="h6" component="div">
                {`${t('participants.claim.pending.sectionTitle')} (${claim.participants.length})`}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {t('participants.claim.pending.sectionDescription')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {t('participants.claim.pending.expiresAt')} {formatDate(claim.expiresAt)}
              </Typography>
            </DashboardToolbarAside>
          </DashboardPageToolbar>
          <PendingClaimParticipantsTable
            participants={claim.participants}
            embedded
            onAbandonParticipant={participantId => {
              const participant = claim.participants.find(p => p.id === participantId);
              const name = participant
                ? `${participant.firstName} ${participant.lastName}`
                : '';
              handleAbandonParticipant(claim, participantId, name);
            }}
          />
        </div>
      ))}
    </>
  );
};

export default PendingClaimsSection;
